import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../AuthContext';
import {
  Plus, Loader2, AlertCircle,
  Edit2, Trash2, BookOpen, X, ArrowLeft, Filter, ChevronDown, Eye, BarChart2, Download
} from 'lucide-react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

import { Tooltip, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PerformanceFormModal from './PerformanceFormModal';
import './ExamAssessmentManager.css';
import configData from '../../configure';

const API_BASE = configData.RESOURCES_API;
export default function ExamAssessmentManager({ orgId }) {
  const { token, userRole, studentInfo, user } = useAuth();
  const studentClassId = studentInfo?.classId || user?.classId || '';

  const navigate = useNavigate();

  const EVENT_START_DATE = '2025-10-05T00:00:00Z';
  const EVENT_END_DATE = '2030-12-31T23:59:59Z';

  const [currentPage, setCurrentPage] = useState('exams');
  const [exams, setExams] = useState([]);
  const [examsFull, setExamsFull] = useState([]);
  const [selectedExamId, setSelectedExamId] = useState(null);
  const [selectedExamName, setSelectedExamName] = useState('');
  const [selectedExamDescription, setSelectedExamDescription] = useState(''); // NEW
  const [assessments, setAssessments] = useState([]);
  const [filteredAssessments, setFilteredAssessments] = useState([]);

  const [loadingExams, setLoadingExams] = useState(true);
  const [loadingAssessments, setLoadingAssessments] = useState(false);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [showPerformanceModal, setShowPerformanceModal] = useState(false);
  const [performanceClassId, setPerformanceClassId] = useState('');
  const [performanceClassName, setPerformanceClassName] = useState('');

  const [classOptions, setClassOptions] = useState([]);
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);

  const [courseNameMap, setCourseNameMap] = useState({});
  const [filterClassIds, setFilterClassIds] = useState([]);
  const [examClassMap, setExamClassMap] = useState({});
  const [filterExamClassIds, setFilterExamClassIds] = useState([]);
  const [totalAssessments, setTotalAssessments] = useState(0);

  // Search for exams
  const [searchQuery, setSearchQuery] = useState('');
  const [schedules, setSchedules] = useState([]);

  // Modals
  const [showCreateExam, setShowCreateExam] = useState(false);
  const [showEditExam, setShowEditExam] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [examToDelete, setExamToDelete] = useState(null);
  const [examToEdit, setExamToEdit] = useState(null);

  const [showAddAssessment, setShowAddAssessment] = useState(false);
  const [showEditAssessment, setShowEditAssessment] = useState(false);
  const [showDeleteAssessmentConfirm, setShowDeleteAssessmentConfirm] = useState(false);
  const [assessmentToEdit, setAssessmentToEdit] = useState(null);
  const [assessmentToDelete, setAssessmentToDelete] = useState(null);
  const [loadingSyllabus, setLoadingSyllabus] = useState(false);
  const [courseToClassesMap, setCourseToClassesMap] = useState({});
  const [customTopic, setCustomTopic] = useState('');

  const [examForm, setExamForm] = useState({
    eventName: '',
    eventType: 'EXAM_TERM',
    eventDescription: '', // NEW
    eventStartDate: '',
    eventEndDate: '',
    assessmentType: 'Test',
    selectedTopics: [],
    subjectTopicsMap: {}, // Maps courseId to array of selected topics
  });

  const [assessmentForm, setAssessmentForm] = useState({
    assessmentName: '',
    assessmentType: 'Test',
    courseId: '',
    classIds: [],
    assessmentDate: '',
    assessmentTime: '',
    selectedTopics: [],
  });

  const [availableTopics, setAvailableTopics] = useState([]);

  // Validation errors
  const [examErrors, setExamErrors] = useState({});
  const [assessmentErrors, setAssessmentErrors] = useState({});

  const classCache = useRef(new Map());

  // Predefined exam names with suggestions
  const predefinedExams = [
    'Unit Test 1', 'Unit Test 2', 'Quarterly Exam', 'Half-Yearly Exam', 'Pre-Final', 'Final Exam'
  ];

  // === DATE VALIDATION ===
  const validateDates = (startDate, endDate) => {
    if (!startDate) return 'Start date is required';
    if (endDate && new Date(endDate) < new Date(startDate)) {
      return 'End date cannot be earlier than start date';
    }
    return null;
  };

  // === EXAM HANDLERS ===
  const confirmDelete = (examId, examName) => {
    setExamToDelete({ eventId: examId, eventName: examName });
    setShowDeleteConfirm(true);
  };

  const deleteExam = async () => {
    if (!examToDelete) return;

    try {
      await axios.delete(`${API_BASE}/events/${examToDelete.eventId}`, {
        headers: { Authorization: token },
      });

      toast.success(`"${examToDelete.eventName}" deleted successfully`);
      setExams(prev => prev.filter(e => e.eventId !== examToDelete.eventId));
      setExamsFull(prev => prev.filter(e => e.eventId !== examToDelete.eventId));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete exam');
    } finally {
      setShowDeleteConfirm(false);
      setExamToDelete(null);
    }
  };

  const openEditExam = (examId) => {
    const exam = examsFull.find(e => e.eventId === examId);
    if (!exam) return;

    setExamToEdit(exam);
    setExamForm({
      eventName: exam.eventName,
      eventType: exam.eventType || 'EXAM_TERM',
      eventDescription: exam.eventDescription || '', // NEW
      eventStartDate: exam.eventStartDate ? new Date(exam.eventStartDate).toISOString().split('T')[0] : '',
      eventEndDate: exam.eventEndDate ? new Date(exam.eventEndDate).toISOString().split('T')[0] : '',
      assessmentType: 'Test',
      selectedTopics: [],
    });
    setExamErrors({});
    setShowEditExam(true);
  };

  const updateExam = async () => {
    if (!examToEdit) return;

    const errors = {};
    if (!examForm.eventName.trim()) errors.eventName = 'Exam name is required';
    if (!examForm.eventStartDate) errors.eventStartDate = 'Start date is required';

    const dateError = validateDates(examForm.eventStartDate, examForm.eventEndDate);
    if (dateError && dateError !== 'Start date is required') errors.dateRange = dateError;

    if (Object.keys(errors).length > 0) {
      setExamErrors(errors);
      return;
    }

    setExamErrors({});
    const toastId = toast.loading('Updating exam...');

    try {
      const startDate = new Date(examForm.eventStartDate);
      const endDate = examForm.eventEndDate ? new Date(examForm.eventEndDate) : startDate;

      const getRes = await axios.get(`${API_BASE}/events/${examToEdit.eventId}`, {
        headers: { Authorization: token }
      });

      const eventData = getRes.data;

      const updatedEvent = {
        ...eventData,
        eventName: examForm.eventName.trim(),
        eventDescription: examForm.eventDescription.trim() || null, // NEW
        eventStartDate: startDate.toISOString(),
        eventEndDate: endDate.toISOString(),
        eventScheduleMap: {
          ...eventData.eventScheduleMap,
          main: eventData.eventScheduleMap?.main?.map(sub => ({
            ...sub,
            subEventName: examForm.eventName.trim(),
            eventDateAndTime: startDate.toISOString(),
          })) || [{
            subEventName: examForm.eventName.trim(),
            eventDateAndTime: startDate.toISOString(),
            durationMins: "180",
            location: "School Campus"
          }]
        }
      };

      await axios.put(`${API_BASE}/events/${examToEdit.eventId}`, updatedEvent, {
        headers: { Authorization: token, 'Content-Type': 'application/json' }
      });

      toast.update(toastId, { render: 'Exam updated successfully!', type: 'success', isLoading: false, autoClose: 3000 });
      setShowEditExam(false);
      setExamToEdit(null);
      setExamForm({ eventName: '', eventType: 'EXAM_TERM', eventDescription: '', eventStartDate: '', eventEndDate: '', assessmentType: 'Test' });
      fetchExams();
    } catch (err) {
      toast.update(toastId, { render: err.response?.data?.message || 'Failed to update exam', type: 'error', isLoading: false, autoClose: 4000 });
    }
  };

  // === ASSESSMENT HANDLERS ===
  const openAddAssessment = () => {
    setAssessmentForm({
      assessmentName: selectedExamName,
      assessmentType: 'Test',
      courseId: '',
      classIds: filterClassIds,
    });
    setSelectedClassIds(filterClassIds);
    setSelectedCourseIds([]);
    setAssessmentErrors({});
    setShowAddAssessment(true);
  };

  const openEditAssessment = async (assessment) => {
    setAssessmentToEdit(assessment);
    setAssessmentForm({
      assessmentName: assessment.assessmentName || '',
      assessmentType: assessment.assessmentType || 'Test',
      courseId: assessment.courseId || '',
      assessmentDate: assessment.assessmentDate || '',
      assessmentTime: assessment.assessmentTime || '',
      selectedTopics: assessment.instructions && Array.isArray(assessment.instructions)
        ? assessment.instructions.filter(i => i !== "Complete all questions carefully.")
        : [],
    });
    setSelectedClassIds(assessment.classIds || []);
    setSelectedCourseIds([assessment.courseId]);
    setAssessmentErrors({});
    setShowEditAssessment(true);
    setLoadingSyllabus(true);

    // Ensure topics are loaded/refreshed for this subject
    if (assessment.courseId) {
      try {
        const r = await axios.get(`${API_BASE}/courses/${assessment.courseId}`, { headers: { Authorization: token } });
        const details = r.data.courseDetails || {};
        const name = details.courseName || 'Unknown Subject';
        const rawCode = r.data.courseCode || details.courseCode || '';
        const code = rawCode.trim() && rawCode.trim() !== 'N/A' ? rawCode.trim() : null;
        const topics = details.topics || [];

        setCourseNameMap(prev => ({
          ...prev,
          [assessment.courseId]: { name, code, topics }
        }));
      } catch (err) {
        console.error("Failed to fetch missing course topics", err);
      } finally {
        setLoadingSyllabus(false);
      }
    } else {
      setLoadingSyllabus(false);
    }
  };

  const confirmDeleteAssessment = (assessment) => {
    setAssessmentToDelete(assessment);
    setShowDeleteAssessmentConfirm(true);
  };

  const deleteAssessment = async () => {
    if (!assessmentToDelete) return;

    try {
      await axios.delete(`${API_BASE}/assessment/${assessmentToDelete.assessmentId}`, {
        headers: { Authorization: token },
      });

      toast.success('Assessment deleted successfully');
      setAssessments(prev => prev.filter(a => a.assessmentId !== assessmentToDelete.assessmentId));
      setFilteredAssessments(prev => prev.filter(a => a.assessmentId !== assessmentToDelete.assessmentId));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete assessment');
    } finally {
      setShowDeleteAssessmentConfirm(false);
      setAssessmentToDelete(null);
    }
  };

  const createAssessment = async () => {
    const errors = {};
    if (!assessmentForm.assessmentName.trim()) errors.assessmentName = 'Assessment name is required';
    if (selectedCourseIds.length === 0) errors.subject = 'Please select at least one course';
    if (selectedClassIds.length === 0) errors.classes = 'Please select at least one class';

    if (Object.keys(errors).length > 0) {
      setAssessmentErrors(errors);
      return;
    }

    setAssessmentErrors({});
    const toastId = toast.loading(selectedCourseIds.length > 1 
      ? `Creating ${selectedCourseIds.length} assessments...` 
      : 'Creating assessment...'
    );

    try {
      // Logic for bulk creation
      for (let i = 0; i < selectedCourseIds.length; i++) {
        const courseId = selectedCourseIds[i];
        if (selectedCourseIds.length > 1) {
          toast.update(toastId, { render: `Creating assessment ${i + 1}/${selectedCourseIds.length}...` });
        }

        await axios.post(`${API_BASE}/assessment`, {
          assessmentName: assessmentForm.assessmentName.trim(),
          eventId: selectedExamId,
          courseId: courseId,
          assessmentType: assessmentForm.assessmentType,
          assessmentDate: assessmentForm.assessmentDate,
          assessmentTime: assessmentForm.assessmentTime,
          academicYear: '2025-2026',
          // Instructions only if single course selected
          instructions: selectedCourseIds.length === 1 && assessmentForm.selectedTopics && assessmentForm.selectedTopics.length > 0
            ? assessmentForm.selectedTopics
            : ["Complete all questions carefully."],
          classIds: selectedClassIds,
        }, {
          headers: { Authorization: token, 'Content-Type': 'application/json' }
        });
      }

      toast.update(toastId, { render: `Created ${selectedCourseIds.length} assessment(s)!`, type: 'success', isLoading: false, autoClose: 3000 });
      setShowAddAssessment(false);
      fetchAssessments(selectedExamId);
      fetchSchedules(selectedExamId);
    } catch (err) {
      toast.update(toastId, { render: err.response?.data?.message || 'Failed to create assessment', type: 'error', isLoading: false, autoClose: 4000 });
    }
  };

  const fetchSchedules = async (eventId) => {
    if (!eventId) return;
    try {
      const effectiveOrgId = orgId || user?.orgId || studentInfo?.orgId || sessionStorage.getItem('orgId') || 'org1';
      const { data } = await axios.get(configData.SCHEDULES || `${API_BASE}/schedules`, {
        params: { filterType: 'event', id: eventId, orgId: effectiveOrgId, pageSize: 100 },
        headers: { Authorization: token },
      });
      const schedulesList = data?.items || data?.content || (Array.isArray(data) ? data : []);
      setSchedules(schedulesList);
    } catch (err) {
      console.error('Failed to fetch schedules', err);
    }
  };

  const courseScheduleMap = React.useMemo(() => {
    const newMap = {};
    schedules.forEach(sch => {
      const classId = String(sch.classId || '');
      const className = classOptions.find(c => String(c.id) === classId)?.name
        || sch.className
        || `${sch.classLabel || ''}`.trim()
        || 'Unknown Class';

      Object.entries(sch.scheduleItems || {}).forEach(([day, items]) => {
        items.forEach(item => {
          const cid = item.courseId;
          const rawDate = item.scheduledDate || '';
          const formattedDate = rawDate.split('-').reverse().join('-');
          
          if (!newMap[cid]) newMap[cid] = [];
          newMap[cid].push({
            classId,
            className,
            date: formattedDate || 'N/A',
            time: item.startTime,
          });
        });
      });
    });
    return newMap;
  }, [schedules, classOptions]);

  const normalizeClassLabel = React.useCallback((value) => (
    String(value || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase()
  ), []);

  const getScheduledSlotsForAssessment = React.useCallback((ass) => {
    const allSlots = courseScheduleMap[ass.courseId] || [];
    const allowedClassIds = new Set((ass.classIds || []).map(id => String(id)));
    const selectedFilterIds = new Set((filterClassIds || []).map(id => String(id)));
    const selectedFilterNames = new Set(
      classOptions
        .filter(option => selectedFilterIds.has(String(option.id)))
        .map(option => normalizeClassLabel(option.name))
    );

    return allSlots.filter((slot) => {
      const slotClassId = String(slot.classId);
      const slotClassName = normalizeClassLabel(slot.className);
      const matchesAssessment = allowedClassIds.size === 0 || allowedClassIds.has(slotClassId);
      const matchesSelectedClass = selectedFilterIds.size === 0
        || selectedFilterIds.has(slotClassId)
        || (selectedFilterNames.size > 0 && selectedFilterNames.has(slotClassName));
      return matchesAssessment && matchesSelectedClass;
    });
  }, [classOptions, courseScheduleMap, filterClassIds, normalizeClassLabel]);

  const updateAssessment = async () => {
    if (!assessmentToEdit) return;

    const errors = {};
    if (!assessmentForm.assessmentName.trim()) errors.assessmentName = 'Assessment name is required';
    if (!assessmentForm.selectedTopics || assessmentForm.selectedTopics.length === 0) {
      errors.syllabus = 'Please add/select the exam syllabus before updating the assessment';
    }

    if (Object.keys(errors).length > 0) {
      setAssessmentErrors(errors);
      return;
    }

    setAssessmentErrors({});
    const toastId = toast.loading('Updating assessment...');

    try {
      const res = await axios.get(`${API_BASE}/assessment/${assessmentToEdit.assessmentId}`, {
        headers: { Authorization: token }
      });
      const current = res.data;

      const updated = {
        ...current,
        assessmentName: assessmentForm.assessmentName.trim(),
        assessmentType: assessmentForm.assessmentType,
        assessmentDate: assessmentForm.assessmentDate,
        assessmentTime: assessmentForm.assessmentTime,
        courseId: assessmentForm.courseId,
        academicYear: '2025-2026',
        instructions: assessmentForm.selectedTopics,
        classIds: selectedClassIds,
      };

      await axios.put(`${API_BASE}/assessment/${assessmentToEdit.assessmentId}`, updated, {
        headers: { Authorization: token, 'Content-Type': 'application/json' }
      });

      toast.update(toastId, { render: 'Assessment updated!', type: 'success', isLoading: false, autoClose: 3000 });
      setShowEditAssessment(false);
      setAssessmentToEdit(null);
      fetchAssessments(selectedExamId);
      fetchSchedules(selectedExamId);
    } catch (err) {
      toast.update(toastId, { render: err.response?.data?.message || 'Failed to update assessment', type: 'error', isLoading: false, autoClose: 4000 });
    }
  };

  // === DATA FETCHING ===
  const fetchClasses = useCallback(async () => {
    if (!token) return;
    setLoadingClasses(true);
    try {
      const { data } = await axios.get(
        configData.get_resources_resources_classes_org(orgId),
        { headers: { Authorization: token } }
      );

      const options = data
        .map(cls => {
          const section = (cls.sectionName && cls.sectionName !== 'null' && cls.sectionName !== 'undefined') ? String(cls.sectionName).trim() : '';
          return {
            id: cls.classId,
            name: (section ? `${cls.className} ${section}` : `${cls.className || ''}`).trim(),
          };
        })
        .sort((a, b) => {
          const getInfo = label => {
            const m = label.match(/Class (\d+)\s*(\w*)/i);
            return m ? { num: +m[1], sec: m[2].toUpperCase() || 'A' } : { num: 0, sec: label };
          };
          const A = getInfo(a.name);
          const B = getInfo(b.name);
          return A.num !== B.num ? A.num - B.num : A.sec.localeCompare(B.sec);
        });

      setClassOptions(options);
    } catch (err) {
      toast.error('Failed to load classes');
    } finally {
      setLoadingClasses(false);
    }
  }, [token, orgId]);

  const fetchCoursesForSelectedClasses = useCallback(async () => {
    if (selectedClassIds.length === 0) {
      setCourses([]);
      setSelectedCourseIds([]);
      return;
    }
    setLoadingCourses(true);
    const tempMap = {};
    const courseList = [];
    const mapping = {};

    try {
      for (const classId of selectedClassIds) {
        if (classCache.current.has(classId)) {
          const cached = classCache.current.get(classId);
          cached.forEach(c => {
            tempMap[c.courseId] = { name: c.courseName, code: c.courseCode };
            if (!mapping[c.courseId]) mapping[c.courseId] = new Set();
            mapping[c.courseId].add(classId);
          });
          courseList.push(...cached);
          continue;
        }

        const res = await axios.get(`${API_BASE}/classes/${classId}`, { headers: { Authorization: token } });
        const courseIds = res.data.courseIds || [];
        const responses = await Promise.all(
          courseIds.map(id =>
            axios.get(`${API_BASE}/courses/${id}`, { headers: { Authorization: token } })
              .catch(() => ({ data: { courseId: id, courseDetails: { courseName: 'Unknown', courseCode: 'N/A' } } }))
          )
        );

        const classCourses = responses.map(r => {
          const details = r.data.courseDetails || {};
          const name = details.courseName || 'Unknown Subject';
          const rawCode = r.data.courseCode || details.courseCode || '';
          const code = rawCode.trim() && rawCode.trim() !== 'N/A' ? rawCode.trim() : null;
          const topics = details.topics || [];

          tempMap[r.data.courseId] = { name, code, topics };

          if (!mapping[r.data.courseId]) mapping[r.data.courseId] = new Set();
          mapping[r.data.courseId].add(classId);

          return { courseId: r.data.courseId, courseName: name, courseCode: code, topics };
        });

        classCache.current.set(classId, classCourses);
        courseList.push(...classCourses);
      }

      const unique = Array.from(new Map(courseList.map(c => [c.courseId, c])).values());

      const finalMapping = {};
      Object.keys(mapping).forEach(cid => {
        finalMapping[cid] = Array.from(mapping[cid]);
      });
      setCourseToClassesMap(finalMapping);

      setCourses(unique);
      setCourseNameMap(prev => ({ ...prev, ...tempMap }));
    } catch (err) {
      toast.error('Failed to load courses');
    } finally {
      setLoadingCourses(false);
    }
  }, [selectedClassIds, token]);

  useEffect(() => {
    if (courses.length > 0 && selectedClassIds.length > 0) {
      const allCourseIds = courses.map(c => c.courseId);
      if (selectedCourseIds.length === 0 || selectedCourseIds.every(id => allCourseIds.includes(id))) {
        setSelectedCourseIds(allCourseIds);
      }
    }
  }, [courses, selectedClassIds]);

  useEffect(() => { fetchCoursesForSelectedClasses(); }, [fetchCoursesForSelectedClasses]);
  useEffect(() => { fetchClasses(); }, [fetchClasses]);

  useEffect(() => {
    if (currentPage === 'assessments' && filterClassIds.length === 0 && classOptions.length > 0) {
      setFilterClassIds([String(classOptions[0].id)]);
    }
  }, [currentPage, classOptions, filterClassIds]);

  useEffect(() => {
    if (userRole === 'STUDENT' && studentClassId && currentPage === 'exams') {
      setFilterExamClassIds([String(studentClassId)]);
    }
  }, [userRole, studentClassId, currentPage]);

  useEffect(() => {
    if (userRole === 'STUDENT' && studentClassId && currentPage === 'assessments') {
      setFilterClassIds([String(studentClassId)]);
    }
  }, [userRole, studentClassId, currentPage]);

  const fetchExams = useCallback(async () => {

    if (!token) return;
    setLoadingExams(true);
    try {
      const response = await axios.get(`${API_BASE}/events`, {
        params: { orgId, eventStartDate: EVENT_START_DATE, eventEndDate: EVENT_END_DATE, pageSize: 100 },
        headers: { Authorization: token },
      });

      const filtered = (response.data.items || [])
        .filter(e => {
          const n = (e.eventName || '').toLowerCase();
          const t = e.eventType || '';
          return t.includes('EXAM') || n.includes('exam') || n.includes('test') || n.includes('term');
        })
        .map(e => ({
          eventId: e.eventId,
          eventName: e.eventName?.trim() || 'Untitled',
          eventStartDate: e.eventStartDate,
          eventEndDate: e.eventEndDate,
          eventType: e.eventType,
          eventDescription: e.eventDescription, // NEW
        }))
        .sort((a, b) => a.eventName.localeCompare(b.eventName));

      setExams(filtered.map(e => ({ eventId: e.eventId, eventName: e.eventName })));
      setExamsFull(filtered);

      // NEW: Fetch class mappings for filtering the exams list
      fetchExamClassMappings(filtered.map(e => e.eventId));
    } catch (err) {
      toast.error('Failed to load exams');
      setExams([]);
      setExamsFull([]);
    } finally {
      setLoadingExams(false);
    }
  }, [token, orgId]);

  useEffect(() => { fetchExams(); }, [fetchExams]);

  const fetchAssessments = async (eventId) => {
    if (!eventId) return;
    setLoadingAssessments(true);
    try {
      const res = await axios.get(`${API_BASE}/assessment/event`, {
        params: { eventId, pageSize: 500 },
        headers: { Authorization: token },
      });
      const items = res.data.items || [];
      setAssessments(items);
      setFilteredAssessments(items);

      const uniqueCourseIds = [...new Set(items.map(a => a.courseId).filter(Boolean))];
      const missingIds = uniqueCourseIds.filter(id => !courseNameMap[id]);

      if (missingIds.length > 0) {
        const promises = missingIds.map(id =>
          axios.get(`${API_BASE}/courses/${id}`, { headers: { Authorization: token } })
            .then(r => {
              const details = r.data.courseDetails || {};
              const name = details.courseName || 'Unknown Subject';
              const rawCode = r.data.courseCode || details.courseCode || '';
              const code = rawCode.trim() && rawCode.trim() !== 'N/A' ? rawCode.trim() : null;
              const topics = details.topics || [];
              return { courseId: id, name, code, topics };
            })
            .catch(() => ({ courseId: id, name: 'Unknown Subject', code: null, topics: [] }))
        );
        const results = await Promise.all(promises);
        const newMap = {};
        results.forEach(r => { newMap[r.courseId] = { name: r.name, code: r.code, topics: r.topics }; });
        setCourseNameMap(prev => ({ ...prev, ...newMap }));
      }
    } catch (err) {
      toast.error('Failed to load assessments');
    } finally {
      setLoadingAssessments(false);
    }
    fetchSchedules(eventId);
  };

  useEffect(() => {
    if (!filterClassIds || filterClassIds.length === 0) {
      setFilteredAssessments([]); // Show nothing if no class is selected
    } else {
      setFilteredAssessments(
        assessments.filter(ass => {
          if (!ass.classIds || ass.classIds.length === 0) return false;
          return ass.classIds.some(id => filterClassIds.includes(String(id)));
        })
      );
    }
  }, [filterClassIds, assessments]);

  const viewAssessments = (examId, examName) => {
    const exam = examsFull.find(e => e.eventId === examId);
    setSelectedExamId(examId);
    setSelectedExamName(examName);
    setSelectedExamDescription(exam?.eventDescription || '');
    setCurrentPage('assessments');

    // Default to the first class if available
    if (classOptions.length > 0) {
      setFilterClassIds([String(classOptions[0].id)]);
    } else {
      setFilterClassIds([]);
    }

    fetchAssessments(examId);
  };

  const backToExams = () => {
    setCurrentPage('exams');
    setSelectedExamId(null);
    setSelectedExamName('');
    setSelectedExamDescription(''); // NEW
    setAssessments([]);
    setFilteredAssessments([]);
    setSearchQuery('');
    setFilterExamClassIds([]);
  };

  const downloadSyllabusPDF = (ass, courseName, courseCode) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    // Header
    doc.setFontSize(22);
    doc.setTextColor(2, 132, 199); // Sky blue
    doc.text('Examination Syllabus', pageWidth / 2, 20, { align: 'center' });

    doc.setDrawColor(226, 232, 240);
    doc.line(20, 25, pageWidth - 20, 25);

    // Exam Details
    doc.setFontSize(14);
    doc.setTextColor(30, 41, 59); // Slate 800
    doc.text(`Exam: ${selectedExamName}`, 20, 35);

    doc.setFontSize(11);
    doc.text(`Course: ${courseName} ${courseCode ? `(${courseCode})` : ''}`, 20, 45);
    doc.text(`Type: ${ass.assessmentType || 'Test'}`, 20, 52);

    // Add Schedule Info
    const scheduledSlots = getScheduledSlotsForAssessment(ass);
    let currentY = 59;
    if (scheduledSlots.length > 0) {
      doc.setFont(undefined, 'bold');
      doc.text('Schedule:', 20, currentY);
      doc.setFont(undefined, 'normal');
      scheduledSlots.forEach(s => {
        currentY += 6;
        doc.text(`${s.className}: ${s.date}`, 25, currentY);
      });
      currentY += 7;
    } else {
      doc.text('Status: Not Scheduled', 20, currentY);
      currentY += 7;
    }

    doc.text(`Date Generated: ${new Date().toLocaleDateString()}`, 20, currentY);

    doc.line(20, currentY + 6, pageWidth - 20, currentY + 6);

    // Syllabus Topics (Offset Y)
    doc.setFontSize(14);
    doc.setTextColor(2, 132, 199);
    doc.text('Syllabus Topics', 20, currentY + 16);

    const examTopics = ass.instructions && Array.isArray(ass.instructions)
      ? ass.instructions.filter(i => i !== "Complete all questions carefully.")
      : [];

    if (examTopics.length > 0) {
      doc.setFontSize(11);
      doc.setTextColor(71, 85, 105); // Slate 600
      let y = currentY + 26;
      examTopics.forEach((topic, index) => {
        if (y > 270) {
          doc.addPage();
          y = 20;
        }
        doc.text(`${index + 1}. ${topic}`, 25, y);
        y += 8;
      });
    } else {
      doc.setFontSize(11);
      doc.setTextColor(148, 163, 184);
      doc.text('General course syllabus applies.', 25, 85);
    }

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text('This is an automatically generated document from the Examination Management System.', pageWidth / 2, 285, { align: 'center' });

    doc.save(`Syllabus_${courseName.replace(/\s+/g, '_')}.pdf`);
  };

  const downloadAllSyllabusPDF = () => {
    if (filteredAssessments.length === 0) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    filteredAssessments.forEach((ass, index) => {
      if (index > 0) doc.addPage();

      const subject = courseNameMap[ass.courseId] || { name: 'Unknown Course', code: null };
      const courseName = subject.name;
      const courseCode = subject.code;

      // Header
      doc.setFontSize(22);
      doc.setTextColor(2, 132, 199); // Sky blue
      doc.text('Examination Syllabus', pageWidth / 2, 20, { align: 'center' });

      doc.setDrawColor(226, 232, 240);
      doc.line(20, 25, pageWidth - 20, 25);

      // Exam Details
      doc.setFontSize(14);
      doc.setTextColor(30, 41, 59); // Slate 800
      doc.text(`Exam: ${selectedExamName}`, 20, 35);

      doc.setFontSize(11);
      doc.text(`Course: ${courseName} ${courseCode ? `(${courseCode})` : ''}`, 20, 45);
      doc.text(`Type: ${ass.assessmentType || 'Test'}`, 20, 52);

      // Add Schedule Info
      const scheduledSlots = getScheduledSlotsForAssessment(ass);
      let currentY = 59;
      if (scheduledSlots.length > 0) {
        doc.setFont(undefined, 'bold');
        doc.text('Schedule:', 20, currentY);
        doc.setFont(undefined, 'normal');
        scheduledSlots.forEach(s => {
          currentY += 6;
          doc.text(`${s.className}: ${s.date}`, 25, currentY);
        });
        currentY += 7;
      } else {
        doc.text('Status: Not Scheduled', 20, currentY);
        currentY += 7;
      }

      doc.text(`Date Generated: ${new Date().toLocaleDateString()}`, 20, currentY);
      doc.line(20, currentY + 6, pageWidth - 20, currentY + 6);

      // Syllabus Topics (Offset Y)
      doc.setFontSize(14);
      doc.setTextColor(2, 132, 199);
      doc.text('Syllabus Topics', 20, currentY + 16);

      const examTopics = ass.instructions && Array.isArray(ass.instructions)
        ? ass.instructions.filter(i => i !== "Complete all questions carefully.")
        : [];

      if (examTopics.length > 0) {
        doc.setFontSize(11);
        doc.setTextColor(71, 85, 105); // Slate 600
        let ty = currentY + 26;
        examTopics.forEach((topic, idx) => {
          if (ty > 270) {
            doc.addPage();
            ty = 20;
          }
          doc.text(`${idx + 1}. ${topic}`, 25, ty);
          ty += 8;
        });
      } else {
        doc.setFontSize(11);
        doc.setTextColor(148, 163, 184);
        doc.text('General course syllabus applies.', 25, currentY + 26);
      }

      // Footer
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text(`Page ${doc.internal.getNumberOfPages()} - ${selectedExamName} Consolidated Syllabus`, pageWidth / 2, pageHeight - 10, { align: 'center' });
    });

    const fileName = filterClassIds.length > 0
      ? `${selectedExamName.replace(/\s+/g, '_')}_Syllabus_Consolidated.pdf`
      : `${selectedExamName.replace(/\s+/g, '_')}_Complete_Syllabus.pdf`;

    doc.save(fileName);
  };

  // NEW: Fetch class mappings for all exams to enable class filtering on the main list
  const fetchExamClassMappings = useCallback(async (examIds) => {
    const classMapping = {};
    try {
      const counts = await Promise.all(examIds.map(async (eventId) => {
        try {
          const res = await axios.get(`${API_BASE}/assessment/event`, {
            params: { eventId, pageSize: 500 },
            headers: { Authorization: token },
          });
          const items = res.data.items || [];
          const classes = new Set();
          items.forEach(item => {
            if (item.classIds && Array.isArray(item.classIds)) {
              item.classIds.forEach(id => classes.add(String(id)));
            }
          });
          classMapping[eventId] = Array.from(classes);

          if (userRole === 'STUDENT' && studentClassId) {
            return items.filter(item => item.classIds?.some(id => String(id) === String(studentClassId))).length;
          }
          return items.length;
        } catch {
          classMapping[eventId] = [];
          return 0;
        }

      }));

      setExamClassMap(classMapping);
      setTotalAssessments(counts.reduce((a, b) => a + b, 0));
    } catch (err) {
      console.error('Failed to fetch class mappings for exams', err);
    }
  }, [token]);

  const createExamWithAssessments = async () => {
    const errors = {};

    if (!examForm.eventName.trim()) errors.eventName = 'Exam name is required';
    if (!examForm.eventStartDate) errors.eventStartDate = 'Start date is required';
    if (selectedClassIds.length === 0) errors.classes = 'Please select at least one class';


    const dateError = validateDates(examForm.eventStartDate, examForm.eventEndDate);
    if (dateError && dateError !== 'Start date is required') errors.dateRange = dateError;

    if (Object.keys(errors).length > 0) {
      setExamErrors(errors);
      return;
    }

    setExamErrors({});
    const toastId = toast.loading(`Creating exam + ${selectedCourseIds.length} assessment subjects...`);

    try {
      const startDate = new Date(examForm.eventStartDate);
      const endDate = examForm.eventEndDate ? new Date(examForm.eventEndDate) : startDate;

      const eventRes = await axios.post(`${API_BASE}/events`, {
        eventName: examForm.eventName.trim(),
        eventType: examForm.eventType,
        eventDescription: examForm.eventDescription.trim() || null, // NEW
        orgId,
        academicYear: '2025-2026',
        eventStartDate: startDate.toISOString(),
        eventEndDate: endDate.toISOString(),
        eventScheduleMap: {
          "main": [{
            subEventName: examForm.eventName.trim(),
            eventDateAndTime: startDate.toISOString(),
            durationMins: "180",
            location: "School Campus"
          }]
        }
      }, { headers: { Authorization: token, 'Content-Type': 'application/json' } });

      const newEventId = eventRes.data.eventId;

      const results = await Promise.allSettled(
        selectedCourseIds.map(courseId => {
          return axios.post(`${API_BASE}/assessment`, {
            assessmentName: examForm.eventName.trim(),
            eventId: newEventId,
            courseId,
            assessmentType: examForm.assessmentType,
            academicYear: '2025-2026',
            instructions: ["Complete all questions carefully."],
            classIds: courseToClassesMap[courseId] || selectedClassIds,
          }, { headers: { Authorization: token, 'Content-Type': 'application/json' } });
        })
      );

      const failed = results.filter(r => r.status === 'rejected').length;
      toast.update(toastId, {
        render: failed === 0
          ? `Exam + ${selectedCourseIds.length} assessments created!`
          : `${selectedCourseIds.length - failed} created, ${failed} failed`,
        type: failed === 0 ? 'success' : 'warning',
        isLoading: false,
        autoClose: 4000
      });

      setShowCreateExam(false);
      setExamForm({ eventName: '', eventType: 'EXAM_TERM', eventDescription: '', eventStartDate: '', eventEndDate: '', assessmentType: 'Test' });
      setSelectedClassIds([]);
      setSelectedCourseIds([]);
      fetchExams();
    } catch (err) {
      toast.update(toastId, { render: 'Creation failed', type: 'error', isLoading: false, autoClose: 4000 });
    }
  };

  if (!token) {
    return (
      <div className="eam-loading-auth">
        <Loader2 className="eam-loading-spinner" />
        <p>Loading authentication...</p>
      </div>
    );
  }

  const filteredExams = exams.filter(exam => {
    const matchesSearch = exam.eventName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesClass = filterExamClassIds.length > 0
      ? filterExamClassIds.some(id => examClassMap[exam.eventId]?.includes(String(id)))
      : true;

    // Status Filter logic
    return matchesSearch && matchesClass;
  });

  const examSuggestions = predefinedExams.filter(name =>
    name.toLowerCase().includes(examForm.eventName.toLowerCase())
  );

  return (
    <div className="eam-main-wrapper">


      <div className="eam-container">
        {currentPage === 'exams' ? (
          <div className="eam-wrapper">
            <div className="eam-page-header">
              <h1 className="eam-page-title">Exam Management</h1>
            </div>

            {/* Stats Row */}
            <div className="eam-stats-row">
              <div className="eam-stat-card">
                <div className="eam-stat-label">Total Exams</div>
                <div className="eam-stat-value">{exams.length}</div>
              </div>
              <div className="eam-stat-card">
                <div className="eam-stat-label">Total Courses</div>
                <div className="eam-stat-value">{totalAssessments}</div>
              </div>
            </div>

            <div className="eam-top-bar">
              <div style={{ width: '220px' }}>
                <CleanDropdown
                  options={classOptions}
                  selected={filterExamClassIds}
                  onChange={setFilterExamClassIds}
                  placeholder="All Classes"
                  singleSelect={true}
                  showSelectAll={false}
                  disabled={userRole === 'STUDENT'}
                />
              </div>

              <div className="eam-search-bar" style={{ flex: userRole === 'STUDENT' ? '1' : 'none' }}>

                <input
                  type="text"
                  placeholder="Search exams..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="eam-search-input"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="eam-search-clear"
                    aria-label="Clear search"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>

              {userRole === 'ADMIN' && (
                <button
                  onClick={() => { setShowCreateExam(true); setExamErrors({}); }}
                  className="eam-create-btn"
                >
                  <Plus size={18} /> Create Exam
                </button>
              )}
            </div>

            <div className="eam-table-container">
              {loadingExams ? (
                <div className="eam-loader-center">
                  <Loader2 className="eam-spinner" />
                </div>
              ) : filteredExams.length === 0 ? (
                searchQuery ? (
                  <div className="eam-empty-state">
                    <AlertCircle className="eam-empty-icon" />
                    <p className="eam-empty-text">No exams found matching "{searchQuery}"</p>
                    <p className="eam-empty-subtext">Try a different search term</p>
                  </div>
                ) : (
                  <div className="eam-empty-state">
                    <AlertCircle className="eam-empty-icon" />
                    <p className="eam-empty-text">No exam groups found</p>
                    <p className="eam-empty-subtext">Create your first exam group to get started</p>
                  </div>
                )
              ) : (
                <table className="eam-table">
                  <thead>
                    <tr>
                      <th>Exam Name</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Description</th>
                      {userRole !== 'STUDENT' && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredExams.map((exam) => {
                      const full = examsFull.find(e => e.eventId === exam.eventId) || {};
                      const start = full.eventStartDate ? new Date(full.eventStartDate).toLocaleDateString('en-GB') : '—';
                      const end = full.eventEndDate ? new Date(full.eventEndDate).toLocaleDateString('en-GB') : '—';

                      return (
                        <tr
                          key={exam.eventId}
                          onClick={() => viewAssessments(exam.eventId, exam.eventName)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td><div className="eam-group-name">{exam.eventName}</div></td>
                          <td>{start}</td>
                          <td>{end}</td>
                          <td className="eam-description-cell">
                            <span className="eam-description-text" title={full.eventDescription || ''}>
                              {full.eventDescription ? full.eventDescription.substring(0, 50) + (full.eventDescription.length > 50 ? '...' : '') : '—'}
                            </span>
                          </td>
                          {userRole !== 'STUDENT' && (
                            <td>
                              <div className="eam-actions">
                                {userRole === 'ADMIN' && (
                                  <>
                                    <button onClick={(e) => { e.stopPropagation(); openEditExam(exam.eventId); }} className="eam-action-btn" title="Edit">
                                      <Edit2 size={18} />
                                    </button>
                                    <button onClick={(e) => { e.stopPropagation(); confirmDelete(exam.eventId, exam.eventName); }} className="eam-action-btn delete" title="Delete">
                                      <Trash2 size={18} />
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        ) : (
          <div className="eam-wrapper">
            <div className="eam-header">
              <div className="eam-header-left">
                <button onClick={backToExams} className="eam-btn-back"><ArrowLeft size={20} /></button>
                <div> {/* NEW wrapper */}
                  <h1 className="eam-title">
                    <BookOpen className="eam-title-icon" /> {selectedExamName}
                  </h1>

                </div>
              </div>
              <div className="eam-header-right">
                <Tooltip title="Download Syllabus for All Courses (PDF)" arrow>
                  <IconButton 
                    className="stuentbutton" 
                    sx={{ border: '1px solid rgb(207, 205, 204)', borderRadius: '4px', padding: '9px 16px', color: '#0070D2' }} 
                    onClick={downloadAllSyllabusPDF}
                    disabled={filteredAssessments.length === 0}
                  >
                    <FileDownloadIcon />
                  </IconButton>
                </Tooltip>

                {userRole !== 'STUDENT' && (
                  <Tooltip title="Add New Course Assessment" arrow>
                    <IconButton 
                      className="stuentbutton" 
                      sx={{ border: '1px solid rgb(207, 205, 204)', borderRadius: '4px', padding: '9px 16px', color: '#0070D2' }} 
                      onClick={openAddAssessment}
                    >
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
            </div>

            <div className="eam-filter-bar">
              <div className="eam-filter-group">
                <Filter size={18} />
                <CleanDropdown
                  options={classOptions}
                  selected={filterClassIds}
                  onChange={setFilterClassIds}
                  placeholder="Select Class"
                  showSelectAll={false}
                  singleSelect={true}
                  disabled={userRole === 'STUDENT'}
                />
              </div>
              <div className="eam-filter-info">
                Showing {filteredAssessments.length} assessment{filteredAssessments.length !== 1 ? 's' : ''}
              </div>
            </div>

            {loadingAssessments ? (
              <div className="eam-loader-center"><Loader2 className="eam-spinner-large" /></div>
            ) : filterClassIds.length === 0 ? (
              <div className="eam-empty-state">
                <Filter className="eam-empty-icon" />
                <p className="eam-empty-text">Select a Class</p>
                <p className="eam-empty-subtext">Please select a class from the filter above to view its assessments.</p>
              </div>
            ) : filteredAssessments.length === 0 ? (
              <div className="eam-empty-state">
                <AlertCircle className="eam-empty-icon" />
                <p className="eam-empty-text">No assessments found</p>
                <p className="eam-empty-subtext">Click "Add Course" to create one</p>
              </div>
            ) : (
              <div className="eam-table-container">
                <table className="eam-table">
                  <thead>
                    <tr>
                      <th>Course Code</th>
                      <th>Course Name</th>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Syllabus</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAssessments.map(ass => {
                      const subject = courseNameMap[ass.courseId] || { name: 'Loading...', code: null };
                      const classes = ass.classIds?.map(id =>
                        classOptions.find(c => c.id === id)?.name || 'Unknown'
                      ).join(', ') || '—';

                      return (
                        <tr key={ass.assessmentId}>

                          <td>{subject.code || '—'}</td>
                          <td>{subject.name} {subject.code ? `(${subject.code})` : ''}</td>
                          <td>
                            {(() => {
                              const scheduledSlots = getScheduledSlotsForAssessment(ass);
                              if (scheduledSlots.length === 0) {
                                return <span className="eam-no-schedule">Not Scheduled</span>;
                              }

                              if (scheduledSlots.length === 1) {
                                const slot = scheduledSlots[0];
                                return (
                                  <div className="eam-schedule-info">
                                    <span className="eam-date">{slot.date}</span>
                                  </div>
                                );
                              }

                              // Multiple classes
                              return (
                                <div className="eam-multi-schedule">
                                  {scheduledSlots.map((slot, idx) => (
                                    <div key={idx} className="eam-schedule-mini-item">
                                      {slot.className}: {slot.date}
                                    </div>
                                  ))}
                                </div>
                              );
                            })()}
                          </td>


                          <td>
                            <span className="eam-status-badge">{ass.assessmentType || 'Test'}</span>
                          </td>
                          <td className="eam-topics-cell">
                            {(() => {
                              const examTopics = ass.instructions && Array.isArray(ass.instructions)
                                ? ass.instructions.filter(i => i !== "Complete all questions carefully.")
                                : [];

                              if (examTopics.length > 0) {
                                return (
                                  <div className="eam-topics-preview">
                                    {examTopics.slice(0, 2).map((topic, index) => (
                                      <span key={index} className="eam-topic-tag">
                                        {topic}
                                      </span>
                                    ))}
                                    {examTopics.length > 2 && (
                                      <span className="eam-more-topics">
                                        +{examTopics.length - 2} more
                                      </span>
                                    )}
                                  </div>
                                );
                              }
                              return (
                                <div className="eam-topics-preview">
                                  <span className="eam-no-topics">Course Syllabus Only</span>
                                  {userRole === 'ADMIN' && (
                                    <button
                                      className="eam-add-syllabus-btn"
                                      onClick={() => openEditAssessment(ass)}
                                    >
                                      <Plus size={14} /> Add Syllabus
                                    </button>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                          <td>
                            <div className="eam-actions">
                              <Tooltip title="Download Syllabus PDF" arrow>
                                <button
                                  onClick={() => downloadSyllabusPDF(ass, subject.name, subject.code)}
                                  className="eam-action-btn"
                                >
                                  <Download size={18} />
                                </button>
                              </Tooltip>
                              {userRole === 'ADMIN' && (
                                <>
                                  <Tooltip title="Edit Assessment Details" arrow>
                                    <button onClick={() => openEditAssessment(ass)} className="eam-action-btn">
                                      <Edit2 size={18} />
                                    </button>
                                  </Tooltip>
                                  <Tooltip title="Delete Assessment" arrow>
                                    <button onClick={() => confirmDeleteAssessment(ass)} className="eam-action-btn delete">
                                      <Trash2 size={18} />
                                    </button>
                                  </Tooltip>
                                </>
                              )}
                            </div>
                          </td>

                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* CREATE EXAM MODAL */}
        {showCreateExam && (
          <div className="eam-modal-overlay" onClick={() => setShowCreateExam(false)}>
            <div className="eam-modal wide" onClick={e => e.stopPropagation()}>
              <div className="eam-modal-header">
                <h2 className="eam-modal-title">Create Exam + Assessments</h2>
                <button onClick={() => setShowCreateExam(false)} className="eam-modal-close"><X size={24} /></button>
              </div>
              <div className="eam-modal-body-scrollable">
                <div className="eam-form-group">
                  <label>Exam Name *</label>
                  <input type="text" placeholder="e.g., Annual Exam"
                    value={examForm.eventName}
                    onChange={e => {
                      setExamForm({ ...examForm, eventName: e.target.value });
                      if (e.target.value.trim()) setExamErrors(prev => ({ ...prev, eventName: '' }));
                    }}
                    className={`eam-input ${examErrors.eventName ? 'eam-input-error' : ''}`}
                  />
                  {/* Suggestion Chips */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
                    {predefinedExams.map(option => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => {
                          setExamForm({ ...examForm, eventName: option });
                          setExamErrors(prev => ({ ...prev, eventName: '' }));
                        }}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          border: '1.5px solid #e2e8f0',
                          backgroundColor: examForm.eventName === option ? '#3b82f6' : 'white',
                          color: examForm.eventName === option ? 'white' : '#64748b',
                          transition: 'all 0.2s',
                        }}
                      >
                        {option}
                      </button>
                    ))}
                  </div>

                  <InlineError message={examErrors.eventName} />
                  <small className="eam-hint">Used as assessment name for all subjects</small>
                </div>

                {/* NEW DESCRIPTION FIELD */}
                <div className="eam-form-group">
                  <label>Description (optional)</label>
                  <textarea
                    placeholder="e.g., Mid-term examinations for classes 6–10 covering syllabus up to chapter 12. Duration: 3 hours per paper."
                    value={examForm.eventDescription}
                    onChange={e => setExamForm({ ...examForm, eventDescription: e.target.value })}
                    className="eam-textarea"
                    rows={3}
                  />
                </div>

                <div className="eam-form-row">
                  <div className="eam-form-group">
                    <label>Start Date *</label>
                    <input type="date" value={examForm.eventStartDate}
                      onChange={e => {
                        setExamForm({ ...examForm, eventStartDate: e.target.value });
                        setExamErrors(prev => ({ ...prev, eventStartDate: '', dateRange: '' }));
                      }}
                      className={`eam-input ${examErrors.eventStartDate || examErrors.dateRange ? 'eam-input-error' : ''}`}
                    />
                    <InlineError message={examErrors.eventStartDate || examErrors.dateRange} />
                  </div>
                  <div className="eam-form-group">
                    <label>End Date</label>
                    <input type="date" value={examForm.eventEndDate} min={examForm.eventStartDate}
                      onChange={e => {
                        setExamForm({ ...examForm, eventEndDate: e.target.value });
                        if (examForm.eventStartDate && (!e.target.value || new Date(e.target.value) >= new Date(examForm.eventStartDate))) {
                          setExamErrors(prev => ({ ...prev, dateRange: '' }));
                        }
                      }}
                      className={`eam-input ${examErrors.dateRange ? 'eam-input-error' : ''}`}
                    />
                  </div>
                </div>

                <div className="eam-form-group">
                  <label>Assessment Type</label>
                  <select value={examForm.assessmentType}
                    onChange={e => setExamForm({ ...examForm, assessmentType: e.target.value })}
                    className="eam-select">
                    <option>Test</option>
                    <option>Quiz</option>
                    <option>Assignment</option>
                    <option>Project</option>
                    <option>Written</option>
                    <option>Online</option>
                  </select>
                </div>

                <div className="eam-form-group">
                  <label>Select Class *</label>
                  {loadingClasses ? (
                    <p className="eam-loading-text">Loading class...</p>
                  ) : (
                    <CleanDropdown
                      options={classOptions}
                      selected={selectedClassIds}
                      onChange={ids => {
                        setSelectedClassIds(ids);
                        if (ids.length > 0) setExamErrors(prev => ({ ...prev, classes: '' }));
                      }}
                      placeholder="Select classes"
                      showSelectAll={true}
                      singleSelect={false}
                    />
                  )}
                  <InlineError message={examErrors.classes} />
                </div>



              </div>

              <div className="eam-modal-actions">
                <button onClick={createExamWithAssessments} className="eam-btn-success">Create</button>
                <button onClick={() => setShowCreateExam(false)} className="eam-btn-secondary">Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* EDIT EXAM MODAL */}
        {showEditExam && (
          <div className="eam-modal-overlay" onClick={() => setShowEditExam(false)}>
            <div className="eam-modal" onClick={e => e.stopPropagation()}>
              <div className="eam-modal-header">
                <h2 className="eam-modal-title">Edit Exam</h2>
                <button onClick={() => setShowEditExam(false)} className="eam-modal-close"><X size={24} /></button>
              </div>
              <div className="eam-modal-body-scrollable">
                <div className="eam-form-group">
                  <label>Exam Name *</label>
                  <input type="text" placeholder="e.g., Annual Exam"
                    value={examForm.eventName}
                    onChange={e => {
                      setExamForm({ ...examForm, eventName: e.target.value });
                      if (e.target.value.trim()) setExamErrors(prev => ({ ...prev, eventName: '' }));
                    }}
                    className={`eam-input ${examErrors.eventName ? 'eam-input-error' : ''}`}
                  />
                  {/* Suggestion Chips */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
                    {predefinedExams.map(option => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => {
                          setExamForm({ ...examForm, eventName: option });
                          setExamErrors(prev => ({ ...prev, eventName: '' }));
                        }}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          border: '1.5px solid #e2e8f0',
                          backgroundColor: examForm.eventName === option ? '#3b82f6' : 'white',
                          color: examForm.eventName === option ? 'white' : '#64748b',
                          transition: 'all 0.2s',
                        }}
                      >
                        {option}
                      </button>
                    ))}
                  </div>

                  <InlineError message={examErrors.eventName} />
                </div>

                {/* NEW DESCRIPTION FIELD */}
                <div className="eam-form-group">
                  <label>Description (optional)</label>
                  <textarea
                    value={examForm.eventDescription}
                    onChange={e => setExamForm({ ...examForm, eventDescription: e.target.value })}
                    className="eam-textarea"
                    rows={3}
                    placeholder="Enter exam description..."
                  />
                </div>

                <div className="eam-form-row">
                  <div className="eam-form-group">
                    <label>Start Date *</label>
                    <input type="date" value={examForm.eventStartDate}
                      onChange={e => {
                        setExamForm({ ...examForm, eventStartDate: e.target.value });
                        setExamErrors(prev => ({ ...prev, eventStartDate: '', dateRange: '' }));
                      }}
                      className={`eam-input ${examErrors.eventStartDate || examErrors.dateRange ? 'eam-input-error' : ''}`}
                    />
                    <InlineError message={examErrors.eventStartDate || examErrors.dateRange} />
                  </div>
                  <div className="eam-form-group">
                    <label>End Date</label>
                    <input type="date" value={examForm.eventEndDate} min={examForm.eventStartDate}
                      onChange={e => {
                        setExamForm({ ...examForm, eventEndDate: e.target.value });
                        if (examForm.eventStartDate && (!e.target.value || new Date(e.target.value) >= new Date(examForm.eventStartDate))) {
                          setExamErrors(prev => ({ ...prev, dateRange: '' }));
                        }
                      }}
                      className={`eam-input ${examErrors.dateRange ? 'eam-input-error' : ''}`}
                    />
                  </div>
                </div>
              </div>

              <div className="eam-modal-actions">
                <button onClick={updateExam} className="eam-btn-success">Update Exam</button>
                <button onClick={() => setShowEditExam(false)} className="eam-btn-secondary">Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* DELETE EXAM CONFIRM */}
        {showDeleteConfirm && (
          <div className="eam-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
            <div className="eam-modal" style={{ maxWidth: '400px' }} onClick={e => e.stopPropagation()}>
              <div className="eam-modal-header">
                <h2 className="eam-modal-title text-red-600">Delete Exam</h2>
                <button onClick={() => setShowDeleteConfirm(false)} className="eam-modal-close"><X size={24} /></button>
              </div>
              <div className="eam-modal-body" style={{ textAlign: 'center', padding: '24px 20px' }}>
                <div style={{
                  backgroundColor: '#fee2e2',
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  color: '#ef4444'
                }}>
                  <AlertCircle size={28} />
                </div>
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>
                  Delete this exam?
                </h3>
                <p style={{ fontSize: '16px', fontWeight: '600', color: '#3b82f6', marginBottom: '0' }}>
                  "{examToDelete?.eventName}"
                </p>
              </div>
              <div className="eam-modal-actions" style={{ justifyContent: 'center', borderTop: 'none', paddingBottom: '24px', gap: '12px' }}>
                <button onClick={() => setShowDeleteConfirm(false)} className="eam-btn-secondary" style={{ padding: '10px 24px', fontSize: '14px' }}>Cancel</button>
                <button onClick={deleteExam} className="eam-btn-danger" style={{ padding: '10px 24px', fontSize: '14px' }}>Delete</button>
              </div>
            </div>
          </div>
        )}

        {/* ADD ASSESSMENT MODAL */}
        {showAddAssessment && (
          <div className="eam-modal-overlay" onClick={() => setShowAddAssessment(false)}>
            <div className="eam-modal wide" onClick={e => e.stopPropagation()}>
              <div className="eam-modal-header">
                <h2 className="eam-modal-title">Add Course to "{selectedExamName}"</h2>
                <button onClick={() => setShowAddAssessment(false)} className="eam-modal-close"><X size={24} /></button>
              </div>
              <div className="eam-modal-body-scrollable">
                <div className="eam-form-group">
                  <label>Assessment Name *</label>
                  <input type="text" value={assessmentForm.assessmentName}
                    onChange={e => {
                      setAssessmentForm({ ...assessmentForm, assessmentName: e.target.value });
                      if (e.target.value.trim()) setAssessmentErrors(prev => ({ ...prev, assessmentName: '' }));
                    }}
                    className={`eam-input ${assessmentErrors.assessmentName ? 'eam-input-error' : ''}`}
                    placeholder="e.g., Mathematics Paper"
                    disabled={true}
                  />
                  <InlineError message={assessmentErrors.assessmentName} />
                </div>



                <div className="eam-form-group">
                  <label>Assessment Type</label>
                  <select value={assessmentForm.assessmentType}
                    onChange={e => setAssessmentForm({ ...assessmentForm, assessmentType: e.target.value })}
                    className="eam-select">
                    <option>Test</option>
                    <option>Quiz</option>
                    <option>Assignment</option>
                    <option>Project</option>
                    <option>Written</option>
                    <option>Online</option>
                  </select>
                </div>

                <div className="eam-form-group">
                  <label>Select Class *</label>
                  {loadingClasses ? (
                    <p className="eam-loading-text">Loading class...</p>
                  ) : (
                    <CleanDropdown
                      options={classOptions}
                      selected={selectedClassIds}
                      onChange={ids => {
                        setSelectedClassIds(ids);
                        if (ids.length > 0) setAssessmentErrors(prev => ({ ...prev, classes: '' }));
                      }}
                      placeholder="Select class"
                      showSelectAll={false}
                      singleSelect={true}
                      disabled={true}
                    />
                  )}
                  <InlineError message={assessmentErrors.classes} />
                </div>
                <div className="eam-form-group">
                  <label>Select Subjects *</label>
                  {loadingCourses ? (
                    <p className="eam-loading-text">Loading subjects...</p>
                  ) : courses.length === 0 ? (
                    <p className="eam-hint">Select classes first to load subjects</p>
                  ) : (
                    <CleanDropdown
                      options={courses.map(c => ({
                        id: c.courseId,
                        name: c.courseCode ? `${c.courseName} (${c.courseCode})` : c.courseName
                      }))}
                      selected={selectedCourseIds}
                      onChange={(ids) => {
                        setSelectedCourseIds(ids);
                        const firstId = ids[0] || '';
                        setAssessmentForm(prev => ({ ...prev, courseId: firstId }));
                        if (ids.length > 0) setAssessmentErrors(prev => ({ ...prev, subject: '' }));
                      }}
                      placeholder="Select subjects"
                      showSelectAll={true}
                      singleSelect={false}
                    />
                  )}
                  <InlineError message={assessmentErrors.subject} />
                </div>

                {/* TOPIC SELECTION (SYLLABUS) - Only for single course selection */}
                {selectedCourseIds.length <= 1 && (
                  <div className="eam-form-group">
                    <label>Exam Syllabus (Selected Topics)</label>
                    {loadingSyllabus ? (
                      <p className="eam-loading-text">Loading syllabus...</p>
                    ) : !assessmentForm.courseId ? (
                      <p className="eam-hint">Select a subject first to view topics</p>
                    ) : (courseNameMap[assessmentForm.courseId]?.topics || []).length > 0 ? (
                      <CleanDropdown
                        options={(courseNameMap[assessmentForm.courseId]?.topics || []).map(t => ({ id: t, name: t }))}
                        selected={assessmentForm.selectedTopics || []}
                        onChange={(ids) => setAssessmentForm(prev => ({ ...prev, selectedTopics: ids }))}
                        placeholder="Select topics"
                        showSelectAll={true}
                      />
                    ) : (
                      <p className="eam-info-box">Course Syllabus Only (No specific topics found)</p>
                    )}
                  </div>
                )}
              </div>

              <div className="eam-modal-actions">
                <button onClick={createAssessment} className="eam-btn-success">Create Assessment</button>
                <button onClick={() => setShowAddAssessment(false)} className="eam-btn-secondary">Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* EDIT ASSESSMENT MODAL */}
        {showEditAssessment && (
          <div className="eam-modal-overlay" onClick={() => setShowEditAssessment(false)}>
            <div className="eam-modal wide" onClick={e => e.stopPropagation()}>
              <div className="eam-modal-header">
                <h2 className="eam-modal-title">Edit Assessment</h2>
                <button onClick={() => setShowEditAssessment(false)} className="eam-modal-close"><X size={24} /></button>
              </div>
              <div className="eam-modal-body-scrollable">
                <div className="eam-form-group">
                  <label>Assessment Name *</label>
                  <input type="text" value={assessmentForm.assessmentName}
                    onChange={e => {
                      setAssessmentForm({ ...assessmentForm, assessmentName: e.target.value });
                      if (e.target.value.trim()) setAssessmentErrors(prev => ({ ...prev, assessmentName: '' }));
                    }}
                    className={`eam-input ${assessmentErrors.assessmentName ? 'eam-input-error' : ''}`}
                  />
                  <InlineError message={assessmentErrors.assessmentName} />
                </div>



                <div className="eam-form-group">
                  <label>Assessment Type</label>
                  <select value={assessmentForm.assessmentType}
                    onChange={e => setAssessmentForm({ ...assessmentForm, assessmentType: e.target.value })}
                    className="eam-select">
                    <option>Test</option>
                    <option>Quiz</option>
                    <option>Assignment</option>
                    <option>Project</option>
                    <option>Written</option>
                    <option>Online</option>
                  </select>
                </div>
                <div className="eam-form-group">
                  <label>Subject</label>
                  <div className="eam-read-only-field">
                    {courseNameMap[assessmentForm.courseId]?.name || 'Unknown Subject'}
                    {courseNameMap[assessmentForm.courseId]?.code && ` (${courseNameMap[assessmentForm.courseId]?.code})`}
                  </div>
                </div>

                {/* TOPIC SELECTION (SYLLABUS) */}
                <div className="eam-form-group">
                  <label>Exam Syllabus (Selected Topics) *</label>
                  {(courseNameMap[assessmentForm.courseId]?.topics || []).length > 0 ? (
                    <CleanDropdown
                      options={(courseNameMap[assessmentForm.courseId]?.topics || []).map(t => ({ id: t, name: t }))}
                      selected={assessmentForm.selectedTopics || []}
                      onChange={(topics) => {
                        setAssessmentForm(prev => ({ ...prev, selectedTopics: topics }));
                        if (topics.length > 0) {
                          setAssessmentErrors(prev => ({ ...prev, syllabus: '' }));
                        }
                      }}
                      placeholder="Select topics for syllabus"
                      showSelectAll={true}
                    />
                  ) : (
                    <p className="eam-no-topics">No topics available for this subject.</p>
                  )}
                  <InlineError message={assessmentErrors.syllabus} />
                </div>
              </div>

              <div className="eam-modal-actions">
                <button onClick={updateAssessment} className="eam-btn-success">Update Assessment</button>
                <button onClick={() => setShowEditAssessment(false)} className="eam-btn-secondary">Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* DELETE ASSESSMENT CONFIRM */}
        {showDeleteAssessmentConfirm && (
          <div className="eam-modal-overlay" onClick={() => setShowDeleteAssessmentConfirm(false)}>
            <div className="eam-modal" style={{ maxWidth: '400px' }} onClick={e => e.stopPropagation()}>
              <div className="eam-modal-header">
                <h2 className="eam-modal-title text-red-600">Delete Assessment</h2>
                <button onClick={() => setShowDeleteAssessmentConfirm(false)} className="eam-modal-close"><X size={24} /></button>
              </div>
              <div className="eam-modal-body" style={{ textAlign: 'center', padding: '24px 20px' }}>
                <div style={{
                  backgroundColor: '#fee2e2',
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  color: '#ef4444'
                }}>
                  <AlertCircle size={28} />
                </div>
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>
                  Delete assessment?
                </h3>
                <p style={{ fontSize: '16px', fontWeight: '600', color: '#3b82f6', marginBottom: '0' }}>
                  "{assessmentToDelete?.assessmentName}"
                </p>
              </div>
              <div className="eam-modal-actions" style={{ justifyContent: 'center', borderTop: 'none', paddingBottom: '24px', gap: '12px' }}>
                <button onClick={() => setShowDeleteAssessmentConfirm(false)} className="eam-btn-secondary" style={{ padding: '10px 24px', fontSize: '14px' }}>Cancel</button>
                <button onClick={deleteAssessment} className="eam-btn-danger" style={{ padding: '10px 24px', fontSize: '14px' }}>Delete</button>
              </div>
            </div>
          </div>
        )}

        {/* PERFORMANCE FORM MODAL */}
        <PerformanceFormModal
          open={showPerformanceModal}
          onClose={() => setShowPerformanceModal(false)}
          classId={performanceClassId}
          className={performanceClassName}
          eventId={selectedExamId}
          academicYear="2025-2026"
          onSuccess={() => {
            toast.success("Performance records saved successfully!");
          }}
          orgId={orgId}
        />
      </div>
    </div>
  );
}

// Inline Error Component
function InlineError({ message }) {
  if (!message) return null;
  return <p className="eam-error-text">{message}</p>;
}

// CleanDropdown Component
function CleanDropdown({ options = [], selected = [], onChange, placeholder, showSelectAll = true, disabled = false, singleSelect = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggle = (id) => {
    if (disabled) return;
    if (singleSelect) {
      onChange([id]);
      setIsOpen(false);
    } else {
      const next = selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id];
      onChange(next);
    }
  };

  const toggleAll = () => {
    if (disabled || singleSelect) return;
    if (selected.length === options.length) {
      onChange([]);
    } else {
      onChange(options.map(o => o.id));
    }
  };

  const allSelected = options.length > 0 && selected.length === options.length;
  const selectedItems = options.filter(o => selected.includes(o.id));

  return (
    <div className={`eam-clean-dropdown ${disabled ? 'disabled' : ''}`} ref={dropdownRef}>
      <div className={`eam-dropdown-header ${disabled ? 'disabled' : ''}`} onClick={() => !disabled && setIsOpen(!isOpen)}>
        <span className="eam-dropdown-placeholder">
          {selected.length === 0
            ? placeholder
            : singleSelect
              ? (selectedItems.length ? selectedItems[0].name : placeholder)
              : selected.length === options.length
                ? 'All selected'
                : `${selected.length} selected`}
        </span>
        <ChevronDown size={18} className={`eam-dropdown-arrow ${isOpen ? 'open' : ''}`} />
      </div>

      {isOpen && !disabled && (
        <div className="eam-dropdown-list">
          {/* All Classes Option */}
          <div
            className={`eam-dropdown-item ${selected.length === 0 ? 'active' : ''}`}
            onClick={() => {
              onChange([]);
              setIsOpen(false);
            }}
            style={{ fontWeight: selected.length === 0 ? '700' : '400', borderBottom: '1px solid #f1f5f9' }}
          >
            <span>All Classes</span>
          </div>

          {showSelectAll && !singleSelect && options.length > 1 && (
            <div className="eam-dropdown-item eam-select-all" onClick={toggleAll}>
              <input type="checkbox" checked={allSelected} readOnly />
              <span><strong>Select All</strong></span>
            </div>
          )}
          {options.map(opt => {
            const isSelected = selected.includes(opt.id);
            return (
              <div
                key={opt.id}
                className={`eam-dropdown-item ${isSelected ? 'active' : ''}`}
                onClick={() => toggle(opt.id)}
              >
                {!singleSelect && (
                  <input
                    type="checkbox"
                    checked={isSelected}
                    readOnly
                  />
                )}
                <span>{opt.name}</span>
              </div>
            );
          })}
        </div>
      )}

      {selected.length > 0 && !singleSelect && (
        <div className="eam-dropdown-tags">
          {selected.length === options.length ? (
            <span className="eam-tag">All Selected</span>
          ) : (
            selectedItems.slice(0, 3).map(item => (
              <span key={item.id} className="eam-tag">
                {item.name.length > 15 ? item.name.substring(0, 15) + '...' : item.name}
                {!disabled && <X size={14} onClick={(e) => { e.stopPropagation(); toggle(item.id); }} />}
              </span>
            ))
          )}
          {selectedItems.length > 3 && (
            <span className="eam-tag">+{selectedItems.length - 3} more</span>
          )}
        </div>
      )}
    </div>
  );
}
